import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  FormControl,
  Select,
  ListItemText
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { APP_DISPLAY_NAME } from '../constants/branding';

const Header = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout, switchOrganization } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [orgSwitchError, setOrgSwitchError] = React.useState('');
  const [orgSwitching, setOrgSwitching] = React.useState(false);

  const organizations = Array.isArray(user?.organizations) ? user.organizations : [];
  const singleOrgLabel =
    organizations.length === 1 ? organizations[0].orgName || 'Organization' : 'Organization';

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const handleOrganizationChange = async (e) => {
    const orgId = Number(e.target.value);
    if (!Number.isFinite(orgId) || orgId === user?.org_id) return;
    setOrgSwitchError('');
    setOrgSwitching(true);
    const result = await switchOrganization(orgId);
    setOrgSwitching(false);
    if (!result.success) {
      setOrgSwitchError(result.message || 'Could not switch organization.');
    }
  };

  return (
    <AppBar
      position="fixed"
      color="primary"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: (theme) =>
          `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        color: 'primary.contrastText',
        boxShadow: 2,
      }}
    >
      <Toolbar variant="dense">
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onMenuClick}
          edge="start"
          size="small"
          sx={{ mr: 1 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          noWrap
          component="div"
          onClick={() => navigate('/')}
          sx={{
            flexGrow: 1,
            fontSize: '1rem',
            fontWeight: 700,
            letterSpacing: '0.28em',
            cursor: 'pointer',
            '&:hover': { opacity: 0.88 },
          }}
        >
          {APP_DISPLAY_NAME}
        </Typography>

        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {organizations.length > 1 ? (
              <FormControl size="small" sx={{ minWidth: 160 }} error={Boolean(orgSwitchError)}>
                <Select
                  value={user.org_id ?? ''}
                  onChange={handleOrganizationChange}
                  disabled={orgSwitching}
                  displayEmpty
                  renderValue={(v) => organizations.find((c) => c.orgId === v)?.orgName ?? 'Organization'}
                  sx={{
                    fontSize: '0.875rem',
                    height: 36,
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    color: 'inherit',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.55)' },
                    '& .MuiSvgIcon-root': { color: 'inherit' },
                  }}
                >
                  {organizations.map((c) => (
                    <MenuItem key={c.orgId} value={c.orgId}>
                      <ListItemText primary={c.orgName} secondary={c.slug} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  minHeight: 36,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 1,
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.35)',
                }}
                aria-label="Organization"
              >
                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  {singleOrgLabel}
                </Typography>
              </Box>
            )}
            {orgSwitchError ? (
              <Typography variant="caption" sx={{ maxWidth: 160, color: 'warning.light' }}>
                {orgSwitchError}
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mr: 1, fontSize: '0.875rem', opacity: 0.95 }}>
              Welcome, {user.first_name}
            </Typography>

            <IconButton
              size="small"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
              </Avatar>
            </IconButton>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose}>
                <AccountCircle sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleClose}>
                <Settings sx={{ mr: 1 }} />
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
